import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Check, X } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [newDriver, setNewDriver] = useState({ name: "", email: "", phone: "", password: "" });
  const [creating, setCreating] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [chosenDriver, setChosenDriver] = useState("");

  useEffect(() => {
    if (user === null) return nav("/login", { state: { next: "/admin" } });
    if (user && user.role !== "admin") return nav("/");
  }, [user, nav]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [q, b, d, r] = await Promise.all([
        api.get("/admin/quotes"),
        api.get("/admin/bookings"),
        api.get("/admin/drivers"),
        api.get("/admin/reviews"),
      ]);
      setQuotes(q.data);
      setBookings(b.data);
      setDrivers(d.data);
      setReviews(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === "admin") refresh();
  }, [user]);

  const updateQuote = async (id, status) => {
    try {
      await api.patch(`/admin/quotes/${id}`, { status });
      toast.success(`Quote ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const moderateReview = async (id, status) => {
    try {
      await api.patch(`/admin/reviews/${id}`, { status });
      toast.success(`Review ${status}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const createDriver = async () => {
    setCreating(true);
    try {
      await api.post("/admin/drivers", newDriver);
      toast.success("Driver created");
      setNewDriver({ name: "", email: "", phone: "", password: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const deleteDriver = async (id) => {
    if (!confirm("Delete this driver?")) return;
    try {
      await api.delete(`/admin/drivers/${id}`);
      toast.success("Driver removed");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const assignDriver = async () => {
    if (!chosenDriver) return toast.error("Choose a driver");
    try {
      await api.patch(`/admin/bookings/${assignFor.id}/assign`, { driver_id: chosenDriver });
      toast.success("Driver assigned");
      setAssignFor(null);
      setChosenDriver("");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16 md:py-20" data-testid="admin-dashboard">
      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Admin</span>
      <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">Operations</h1>

      <Tabs defaultValue="bookings" className="mt-10">
        <TabsList>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="admin-tab-quotes">Quotes ({quotes.length})</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="admin-tab-drivers">Drivers ({drivers.length})</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Reviews ({reviews.filter(r => r.status === "pending").length})</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-6">
          {loading ? <p>Loading…</p> : bookings.length === 0 ? (
            <p className="text-muted-foreground">No bookings yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {bookings.map((b) => {
                const drv = drivers.find((d) => d.id === b.assigned_driver_id);
                return (
                  <Card key={b.id} className="p-5 border-border" data-testid={`admin-booking-${b.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-xs text-muted-foreground">{b.pickup_date} · {b.distance_miles}mi</p>
                        <h3 className="font-display text-base mt-1">{b.pickup_location} → {b.dropoff_location}</h3>
                      </div>
                      <Badge>{b.status.replace("_", " ")}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">{b.customer_name} · {b.customer_email}</p>
                    <p className="text-xs text-muted-foreground">{b.pet_name} · {b.pet_type}</p>
                    <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                      <p className="font-display text-xl">£{b.total_price}</p>
                      <Badge variant={b.payment_status === "paid" ? "default" : "secondary"}>{b.payment_status}</Badge>
                    </div>
                    <div className="mt-3 text-xs">
                      <p className="text-muted-foreground">Driver: <span className="text-foreground">{drv ? drv.name : "Unassigned"}</span></p>
                    </div>
                    {b.payment_status === "paid" && (
                      <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setAssignFor(b)} data-testid={`admin-assign-btn-${b.id}`}>
                        {drv ? "Reassign driver" : "Assign driver"}
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quotes" className="mt-6">
          {loading ? <p>Loading…</p> : quotes.length === 0 ? (
            <p className="text-muted-foreground">No quotes yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {quotes.map((q) => (
                <Card key={q.id} className="p-5 border-border" data-testid={`admin-quote-${q.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</p>
                      <h3 className="font-display text-base mt-1">{q.pickup_location} → {q.dropoff_location}</h3>
                    </div>
                    <Badge>{q.status}</Badge>
                  </div>
                  <p className="text-sm mt-3">{q.customer_name} · <span className="text-muted-foreground">{q.customer_email}</span> {q.customer_phone && `· ${q.customer_phone}`}</p>
                  <p className="text-xs text-muted-foreground mt-1">{q.distance_miles} mi · {q.vehicle}</p>
                  {q.pet_info && <p className="text-xs text-muted-foreground mt-1">Pet: {q.pet_info}</p>}
                  <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                    <p className="font-display text-xl">£{q.total_price}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateQuote(q.id, "rejected")} data-testid={`quote-reject-${q.id}`}><X size={14} /></Button>
                      <Button size="sm" onClick={() => updateQuote(q.id, "approved")} data-testid={`quote-approve-${q.id}`}><Check size={14} /></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="drivers" className="mt-6">
          <Dialog>
            <DialogTrigger asChild>
              <Button data-testid="admin-add-driver-btn"><Plus size={16} className="mr-2" /> Add driver</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create driver account</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name</Label><Input value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} data-testid="driver-name-input" /></div>
                <div><Label>Email</Label><Input type="email" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} data-testid="driver-email-input" /></div>
                <div><Label>Phone</Label><Input value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} /></div>
                <div><Label>Password</Label><Input type="password" value={newDriver.password} onChange={(e) => setNewDriver({ ...newDriver, password: e.target.value })} data-testid="driver-password-input" /></div>
              </div>
              <DialogFooter>
                <Button onClick={createDriver} disabled={creating} data-testid="driver-create-btn">{creating ? "Creating…" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {drivers.map((d) => (
              <Card key={d.id} className="p-5 border-border">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-display text-base">{d.name}</h3>
                    <p className="text-xs text-muted-foreground">{d.email}</p>
                    {d.phone && <p className="text-xs text-muted-foreground">{d.phone}</p>}
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteDriver(d.id)} data-testid={`driver-delete-${d.id}`}><Trash2 size={16} /></Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews" className="mt-6">
          <div className="space-y-6">
            {["pending", "approved", "rejected"].map((bucket) => {
              const items = reviews.filter((r) => r.status === bucket);
              if (items.length === 0) return null;
              return (
                <div key={bucket}>
                  <h3 className="font-display text-lg mb-3 capitalize">{bucket} ({items.length})</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((r) => (
                      <Card key={r.id} className="p-5 border-border" data-testid={`admin-review-${r.id}`}>
                        <div className="flex justify-between items-start gap-3">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">{r.route_from} → {r.route_to}</p>
                            <p className="mt-2 font-medium">{r.customer_name}</p>
                          </div>
                          <span className="text-amber-400 text-sm">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                        </div>
                        {r.title && <p className="mt-3 font-display">{r.title}</p>}
                        <p className="mt-2 text-sm text-muted-foreground">{r.body}</p>
                        {bucket === "pending" && (
                          <div className="mt-4 flex gap-2 justify-end">
                            <Button size="sm" variant="outline" onClick={() => moderateReview(r.id, "rejected")} data-testid={`review-reject-${r.id}`}><X size={14} /></Button>
                            <Button size="sm" onClick={() => moderateReview(r.id, "approved")} data-testid={`review-approve-${r.id}`}><Check size={14} /></Button>
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
            {reviews.length === 0 && <p className="text-muted-foreground">No reviews yet.</p>}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign driver</DialogTitle></DialogHeader>
          {assignFor && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{assignFor.pickup_location} → {assignFor.dropoff_location} on {assignFor.pickup_date}</p>
              <Select value={chosenDriver} onValueChange={setChosenDriver}>
                <SelectTrigger data-testid="assign-driver-select"><SelectValue placeholder="Choose driver" /></SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button onClick={assignDriver} data-testid="assign-driver-confirm-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
